import { Repair } from '../types/typesRepair';
import { formatDate, formatTime } from './date';

/** שלבי הסטפר — סדר קבוע */
export const REPAIR_STATUS_STEPS = [
  'התקבל, ממתין לטיפול',
  'ממתין לאישור לקוח',
  'סיום טיפול טכנאי',
  'ממתין למשלוח חזרה',
  'חזר ללקוח',
] as const;

export type RepairStatusStep = (typeof REPAIR_STATUS_STEPS)[number];

function isValidIso(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? '' : raw;
}

function firstValid(...values: Array<string | null | undefined>): string {
  for (const v of values) {
    const ok = isValidIso(v);
    if (ok) return ok;
  }
  return '';
}

function normalizeStatusKey(status: string): string {
  const s = String(status || '').trim();
  if (s === 'התקבל במעבדה' || s === 'ממתין לטיפול') return 'התקבל, ממתין לטיפול';
  if (s === 'חזר לחנות') return 'חזר ללקוח';
  return s;
}

/**
 * מפה של תאריך/שעה לכל שלב סטטוס שהושג.
 * עדיפות: statusReachedAt → שדות אבן־דרך → statusChangedAt לסטטוס הנוכחי.
 */
export function buildRepairStatusDates(repair: Repair | null | undefined): Record<string, string> {
  if (!repair) return {};
  const reached =
    repair.statusReachedAt && typeof repair.statusReachedAt === 'object'
      ? repair.statusReachedAt
      : {};

  const out: Record<string, string> = {};

  out['התקבל, ממתין לטיפול'] = firstValid(
    reached['התקבל, ממתין לטיפול'],
    reached['ממתין לטיפול'],
    repair.receivedDate,
    repair.createdAt,
  );

  out['ממתין לאישור לקוח'] = firstValid(reached['ממתין לאישור לקוח']);

  out['סיום טיפול טכנאי'] = firstValid(
    reached['סיום טיפול טכנאי'],
    repair.technicianCompletedDate,
    repair.labResultStatusDate,
    repair.labSummaryDate,
  );

  out['ממתין למשלוח חזרה'] = firstValid(
    reached['ממתין למשלוח חזרה'],
    repair.treatmentEndDate,
  );

  out['חזר ללקוח'] = firstValid(
    reached['חזר ללקוח'],
    reached['חזר לחנות'],
    repair.returnedToCustomerDate,
  );

  const current = normalizeStatusKey(repair.status || '');
  const changedAt = isValidIso(repair.statusChangedAt);
  if (current && changedAt && !out[current]) {
    out[current] = changedAt;
  }

  // נקה ריקים
  Object.keys(out).forEach((k) => {
    if (!out[k]) delete out[k];
  });
  return out;
}

/** תצוגה קומפקטית: תאריך בשורה + שעה מתחת */
export function formatStatusDateParts(iso?: string | null): { date: string; time: string } | null {
  const raw = isValidIso(iso);
  if (!raw) return null;
  const date = formatDate(raw);
  const time = formatTime(raw);
  if (!date && !time) return null;
  return { date, time };
}
